import { UserData } from "@api/types";

export type SocialAuthResponse = {
  success: boolean;
  userData?: UserData;
  error?: string;
};

export type SocialProvider = "google" | "vk" | "yandex";

// Настройки для провайдеров социальных сетей
const providers = {
  google: {
    name: "Google",
    authUrl: import.meta.env.VITE_API_BASE_URL + "/auth/google",
    popupWidth: 600,
    popupHeight: 600,
  },
  vk: {
    name: "ВКонтакте",
    authUrl: import.meta.env.VITE_API_BASE_URL + "/auth/vk",
    popupWidth: 650,
    popupHeight: 550,
  },
  yandex: {
    name: "Яндекс",
    authUrl: import.meta.env.VITE_API_BASE_URL + "/auth/yandex",
    popupWidth: 600,
    popupHeight: 650,
  },
};

// Создает случайный идентификатор состояния для защиты от CSRF
const generateStateParam = (): string => {
  return Math.random().toString(36).substring(2, 15);
};

// Сохраняет состояние для последующей проверки
const saveState = (state: string): void => {
  sessionStorage.setItem("auth_state", state);
};

// Получает URL авторизации для указанного провайдера
const getAuthorizationUrl = (provider: SocialProvider): string => {
  // Генерируем state для защиты от CSRF атак
  const state = generateStateParam();
  saveState(state);

  // Формируем URL для авторизации через API виджета
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  const authUrl = `${baseUrl}/widget/oauth/${provider}/authorize?state=${state}`;

  return authUrl;
};

// Проверяет, что состояние совпадает с сохраненным
const verifyState = (state: string): boolean => {
  // Если state имеет значение "Отсутствует", пропускаем проверку
  if (state === "Отсутствует") {
    console.warn(
      "Получен state со значением 'Отсутствует'. Пропускаем проверку state."
    );
    return true;
  }

  const savedState = sessionStorage.getItem("auth_state");
  return savedState === state;
};

// Открывает popup для аутентификации
const openAuthPopup = (
  provider: SocialProvider
): Promise<SocialAuthResponse> => {
  return new Promise((resolve) => {
    // Генерируем и сохраняем state для предотвращения CSRF атак
    const state = generateStateParam();
    saveState(state);

    // Формируем URL для авторизации
    const authUrl = getAuthorizationUrl(provider);

    // Открываем popup с авторизацией
    const { popupWidth, popupHeight } = providers[provider];
    const left = window.screenX + (window.outerWidth - popupWidth) / 2;
    const top = window.screenY + (window.outerHeight - popupHeight) / 2.5;
    const popup = window.open(
      authUrl,
      `${provider}_auth`,
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top}`
    );

    // Функция для обработки сообщений от popup
    const handleMessage = (event: MessageEvent) => {
      try {
        console.log("handleMessage event", event);
        let data;
        if (typeof event.data === "string") {
          data = JSON.parse(event.data);
        } else {
          data = event.data;
        }

        console.log("handleMessage event data", data);

        if (data.type === "DEBUG_LOG") {
          console.log(`[Auth Debug] ${data.message}:`, data.data);
          return;
        }

        // Проверяем, что это сообщение от нашего auth callback
        if (data.type === "AUTH_CALLBACK") {
          // Очищаем обработчик событий
          window.removeEventListener("message", handleMessage);

          // Проверяем успешность авторизации
          if (data.success) {
            // Проверяем state для защиты от CSRF
            if (verifyState(data.state)) {
              // Формируем данные пользователя из ответа
              resolve({
                success: true,
                userData: {
                  name: data.name,
                  email: data.email,
                  provider: data.provider,
                  providerId: data.providerId,
                },
              });
            } else {
              resolve({
                success: false,
                error: "Ошибка безопасности: неверный state",
              });
            }
          } else {
            resolve({
              success: false,
              error: data.error || "Ошибка аутентификации",
            });
          }

          // Закрываем popup, если он все еще открыт
          if (popup && !popup.closed) {
            popup.close();
          }
        }
      } catch (error) {
        console.error("Ошибка при обработке сообщения:", error);
      }
    };

    // Устанавливаем слушатель сообщений
    window.addEventListener("message", handleMessage);

    // Периодически проверяем, не закрыл ли пользователь popup
    const checkPopupClosed = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(checkPopupClosed);
        window.removeEventListener("message", handleMessage);

        // Если popup закрыт без успешной авторизации
        if (!popup || popup.closed) {
          resolve({
            success: false,
            error: "Авторизация отменена",
          });
        }
      }
    }, 500);
  });
};

// Аутентификация через социальную сеть
const authenticateWithSocial = async (
  provider: SocialProvider
): Promise<SocialAuthResponse> => {
  try {
    return await openAuthPopup(provider);
  } catch (error) {
    console.error(`Ошибка аутентификации через ${provider}:`, error);
    return {
      success: false,
      error: `Ошибка аутентификации через ${provider}`,
    };
  }
};

export const socialAuthService = {
  authenticateWithSocial,
  getAuthorizationUrl,
};
